import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { WizardDefinition, WizardEndReason } from './types';
import {
  findInitialWizardStepIndex,
  findWizardStepIndexWithTarget,
} from './targetUtils';
import { useWizardsAllowed } from './wizardViewport';

export type WizardContextValue = {
  activeWizard: WizardDefinition | null;
  stepIndex: number;
  startWizard: (definition: WizardDefinition) => void;
  endWizard: (reason: WizardEndReason) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
};

const WizardContext = createContext<WizardContextValue | null>(null);

export function WizardProvider({
  children,
  onWizardEnd,
}: {
  children: ReactNode;
  onWizardEnd?: (wizardId: string, reason: WizardEndReason) => void;
}) {
  const [activeWizard, setActiveWizard] = useState<WizardDefinition | null>(
    null,
  );
  const [stepIndex, setStepIndex] = useState(0);
  const activeWizardIdRef = useRef<string | null>(null);
  const wizardsAllowed = useWizardsAllowed();

  const endWizard = useCallback(
    (reason: WizardEndReason) => {
      const wizardId = activeWizardIdRef.current;
      setActiveWizard(null);
      setStepIndex(0);
      activeWizardIdRef.current = null;
      if (wizardId) {
        onWizardEnd?.(wizardId, reason);
      }
    },
    [onWizardEnd],
  );

  const startWizard = useCallback(
    (definition: WizardDefinition) => {
      if (!wizardsAllowed) {
        return;
      }

      const initialIndex = findInitialWizardStepIndex(definition);
      if (initialIndex === null) {
        onWizardEnd?.(definition.id, 'skipped');
        return;
      }

      activeWizardIdRef.current = definition.id;
      setActiveWizard(definition);
      setStepIndex(initialIndex);
    },
    [onWizardEnd, wizardsAllowed],
  );

  useEffect(() => {
    if (!wizardsAllowed && activeWizard) {
      endWizard('skipped');
    }
  }, [activeWizard, endWizard, wizardsAllowed]);

  const goToNextStep = useCallback(() => {
    if (!activeWizard) {
      return;
    }

    const nextIndex = findWizardStepIndexWithTarget(
      activeWizard,
      stepIndex + 1,
      1,
    );
    if (nextIndex === null) {
      endWizard('completed');
      return;
    }
    setStepIndex(nextIndex);
  }, [activeWizard, endWizard, stepIndex]);

  const goToPreviousStep = useCallback(() => {
    if (!activeWizard) {
      return;
    }

    const previousIndex = findWizardStepIndexWithTarget(
      activeWizard,
      stepIndex - 1,
      -1,
    );
    if (previousIndex === null) {
      return;
    }
    setStepIndex(previousIndex);
  }, [activeWizard, stepIndex]);

  const value = useMemo(
    (): WizardContextValue => ({
      activeWizard,
      stepIndex,
      startWizard,
      endWizard,
      goToNextStep,
      goToPreviousStep,
    }),
    [
      activeWizard,
      endWizard,
      goToNextStep,
      goToPreviousStep,
      startWizard,
      stepIndex,
    ],
  );

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
}

export function useWizardOptional() {
  return useContext(WizardContext);
}
